import '../../../../shared/component/flow-item/view/flow-item.css';

import { IonIcon } from '@ionic/react';
import {
  colorPaletteOutline,
  enterOutline,
  exitOutline,
  home,
  stopwatch,
} from 'ionicons/icons';
import { ReactElement, ReactNode } from 'react';
import { FlowItemBase } from '../../../../shared/component/flow-item/view/flow-item-base';
import { FlowItemProps } from '../../../../shared/component/flow-item/view/flow-item.types';
import { moveItem } from '../../../../shared/component/icon/move-item';
import { reportProduction } from '../../../../shared/component/icon/report-production';
import { waitForOperator } from '../../../../shared/component/icon/wait-for-operator';
import {
  FlowItem,
  FlowItemParameter,
  FlowItemRef,
} from '../../../../shared/model/flow.types';
import { cuttingLinearCutIcon } from '../../../cutting/component/icon/cutting-linear-cut-icon';
import { cuttingRadialCutIcon } from '../../../cutting/component/icon/cutting-radial-cut-icon';
import { BeaconPaintingData } from '../../model/beacon-painting-data';

const PaintingFlowItemComponent = (props: FlowItemProps): ReactElement => {
  const {
    actions,
    flowItem,
    isItemSelected,
    isSectionSelected,
    canAddItem,
    canReorderItem,
    highlight,
    onAddItemsBefore,
    onAddItemsAfter,
    onSelectItem,
    system,
    executionInfo,
    isStartItem,
    isInactive,
    onLongPress,
    label,
    presentItemQuickActions,
    addPrecedingStem,
  } = props;

  const icon = getFlowItemIconFromItem(flowItem);

  return (
    <FlowItemBase
      flowItem={flowItem}
      canAddItem={canAddItem}
      canReorderItem={canReorderItem}
      isItemSelected={isItemSelected}
      isSectionSelected={isSectionSelected}
      highlight={highlight}
      onAddItemsBefore={onAddItemsBefore}
      onAddItemsAfter={onAddItemsAfter}
      onSelectItem={onSelectItem}
      icon={icon}
      actions={actions}
      system={system}
      executionInfo={executionInfo}
      isStartItem={isStartItem}
      isInactive={isInactive}
      onLongPress={onLongPress}
      label={label}
      presentItemQuickActions={presentItemQuickActions}
      addPrecedingStem={addPrecedingStem}
    />
  );
};

const getFlowItemIconFromItem = (flowItem: FlowItem): ReactNode => {
  const waitForType = getWaitForType(flowItem);
  const paintType = flowItem.parameters.find(
    (p) => p.ref === BeaconPaintingData.FlowItemParameterRef.PAINT_TYPE
  );
  return getFlowItemIcon(flowItem.ref, paintType?.value, waitForType?.value);
};

export const getWaitForType = (
  flowItem: FlowItem
): FlowItemParameter<BeaconPaintingData.WaitForInputType> | undefined => {
  return flowItem.parameters.find(
    (p) =>
      p.ref === BeaconPaintingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT
  );
};

export const getFlowItemIcon = (
  flowItemRef: FlowItemRef,
  paintType?: BeaconPaintingData.PaintTypeEnum,
  waitForType?: BeaconPaintingData.WaitForInputType
): ReactNode => {
  let icon: ReactNode = <div>?</div>;
  const classList: string[] = ['flow-item-icon', 'full-hw', 'center'];

  if (flowItemRef === BeaconPaintingData.FlowItemRef.PAINT) {
    const paintClassList: string[] = [...classList, 'bg-danger'];
    let paintIcon = colorPaletteOutline;

    if (paintType === BeaconPaintingData.PaintTypeEnum.LINEAR) {
      paintIcon = cuttingLinearCutIcon;
    } else if (paintType === BeaconPaintingData.PaintTypeEnum.RADIAL) {
      paintIcon = cuttingRadialCutIcon;
    }

    icon = (
      <div className={paintClassList.join(' ')}>
        <IonIcon icon={paintIcon} size="large" />
      </div>
    );
  } else if (flowItemRef === BeaconPaintingData.FlowItemRef.MOVE) {
    icon = <IonIcon icon={moveItem} size="large" />;
  } else if (flowItemRef === BeaconPaintingData.FlowItemRef.WAIT_FOR) {
    if (waitForType === BeaconPaintingData.WaitForInputType.TIME) {
      icon = (
        <div className={[...classList, 'bg-alt'].join(' ')}>
          <IonIcon icon={stopwatch} size="large" />
        </div>
      );
    } else if (waitForType === BeaconPaintingData.WaitForInputType.SENSOR) {
      icon = (
        <div className={[...classList, 'bg-alt'].join(' ')}>
          <IonIcon icon={enterOutline} size="large" />
        </div>
      );
    } else {
      icon = (
        <div className={[...classList, 'bg-alt'].join(' ')}>
          <IonIcon icon={waitForOperator} size="large" />
        </div>
      );
    }
  } else if (
    flowItemRef === BeaconPaintingData.FlowItemRef.PRODUCTION_REPORTING
  ) {
    icon = (
      <div className={[...classList, 'bg-success'].join(' ')}>
        <IonIcon icon={reportProduction} size="large" />
      </div>
    );
  } else if (flowItemRef === BeaconPaintingData.FlowItemRef.MOVE_HOME) {
    icon = (
      <div className={classList.join(' ')}>
        <IonIcon icon={home} size="large" />
      </div>
    );
  } else if (flowItemRef === BeaconPaintingData.FlowItemRef.TRIGGER_OUTPUT) {
    icon = (
      <div className={[...classList, 'bg-alt'].join(' ')}>
        <IonIcon icon={exitOutline} size="large" />
      </div>
    );
  }

  return icon;
};

export { PaintingFlowItemComponent };
